import { IconZoomIn, IconZoomOut, IconZoomReset } from "@tabler/icons-react";
import { AnimatePresence } from "motion/react";
import { useState } from "react";

import type { Cell } from "@edclub/crosswords-engine";
import { cn } from "@edclub/utils";

import { useCrosswordStore } from "@/store";

import { CrosswordCell } from "./CrosswordCell";

export function Crossword() {
  const crossword = useCrosswordStore.use.crossword();
  const setActiveCell = useCrosswordStore.use.setActiveCell();
  const activeCell = useCrosswordStore.use.activeCell();
  const activeWordId = useCrosswordStore.use.activeWordId();
  const setActiveWord = useCrosswordStore.use.setActiveWord();
  const [cellSize, setCellSize] = useState(40);

  if (!crossword) {
    return (
      <div className="relative grid h-full place-content-center place-items-center gap-0.5 overflow-auto rounded-md bg-[#f2f2f2] shadow">
        {t("Missing or invalid config...")}
      </div>
    );
  }

  const cluePositions: { [key: string]: string } = {};

  Object.entries(crossword.across).forEach(([num, clue]) => {
    const key = `${clue.row}-${clue.col}`;
    cluePositions[key] = num;
  });

  Object.entries(crossword.down).forEach(([num, clue]) => {
    const key = `${clue.row}-${clue.col}`;

    if (!cluePositions[key]) {
      cluePositions[key] = num;
    }
  });

  const handleSetActiveCell = (playable: boolean, cell: Cell) => () => {
    if (playable) {
      setActiveCell(cell);
      setActiveWord(cell.words[0]);
    }
  };

  return (
    <div
      className="relative grid h-full place-content-center place-items-center gap-0.5 overflow-auto rounded-md bg-[#f2f2f2] shadow"
      style={{
        gridTemplateColumns: `repeat(0, 55px)`,
      }}
    >
      <div className="absolute top-2 right-2 z-10 flex gap-1 text-[#42576e]">
        <IconZoomReset
          className={cn(
            "h-8 w-8 cursor-pointer rounded-md bg-white p-1 shadow transition-colors hover:bg-gray-100",
          )}
          type="button"
          onClick={() => setCellSize(40)}
        />
        <IconZoomOut
          className={cn(
            "h-8 w-8 cursor-pointer rounded-md bg-white p-1 shadow transition-colors hover:bg-gray-100",
            cellSize <= 25 && "cursor-default opacity-50 select-none",
          )}
          type="button"
          onClick={() => {
            if (cellSize > 25) {
              setCellSize((prev) => prev - 5);
            }
          }}
        />
        <IconZoomIn
          className={cn(
            "h-8 w-8 cursor-pointer rounded-md bg-white p-1 shadow transition-colors hover:bg-gray-100",
            cellSize >= 60 && "cursor-default opacity-50 select-none",
          )}
          type="button"
          onClick={() => {
            if (cellSize < 60) {
              setCellSize((prev) => prev + 5);
            }
          }}
        />
      </div>
      <AnimatePresence>
        {crossword.grid.map((row, rowIndex) => (
          <div key={`row-${rowIndex}`} className="flex gap-0.5">
            {row.map((cell, colIndex) => (
              <CrosswordCell
                key={cell.coord || `cell-${rowIndex}-${colIndex}`}
                rowIndex={rowIndex}
                colIndex={colIndex}
                cell={cell}
                activeWordId={activeWordId}
                activeCell={activeCell}
                cellSize={cellSize}
                handleSetActiveCell={handleSetActiveCell}
              />
            ))}
          </div>
        ))}
      </AnimatePresence>
    </div>
  );
}
